import React, { useEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { PERIODS, countWord } from '../shared/agentActivity';
import JoinRequests from './JoinRequests';

const TEAL = '#0f766e';
const TEAL_BRIGHT = '#59c4c4';
const ORANGE = '#f59e0b';
const ORANGE_INK = '#c2410c';

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
}

function useCountUp(target, reduced) {
  const [value, setValue] = useState(reduced ? target : 0);
  useEffect(() => {
    if (reduced) {
      setValue(target);
      return undefined;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / 650);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, reduced]);
  return value;
}

function Sparkline({ points }) {
  const values = points && points.length ? points : [0];
  const max = Math.max.apply(null, values.concat([1]));
  const width = 120;
  const height = 36;
  const step = values.length === 1 ? 0 : width / (values.length - 1);
  const d = values.map((point, index) => {
    const x = index * step;
    const y = height - (point / max) * (height - 4) - 2;
    return (index === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1);
  }).join(' ');
  return (
    <svg viewBox={'0 0 ' + width + ' ' + height} width="100%" height="36" aria-hidden="true">
      <path d={d} fill="none" stroke={TEAL_BRIGHT} strokeWidth="2" />
    </svg>
  );
}

function Bars({ rows, colors }) {
  const max = Math.max.apply(null, rows.map((row) => row.value).concat([1]));
  return (
    <div>
      {rows.map((row) => (
        <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.28rem' }}>
          <span style={{ width: '3.4rem', fontSize: '0.68rem', color: colors.textSecondary }}>{row.label}</span>
          <span style={{ flex: '1 1 auto', height: '6px', borderRadius: '999px', background: colors.border, overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: Math.round((row.value / max) * 100) + '%', background: TEAL_BRIGHT }} />
          </span>
          <span style={{ width: '1.4rem', textAlign: 'right', fontSize: '0.68rem', fontWeight: 700 }}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function StatTile({ item, open, onToggle, reduced, colors, isDark }) {
  const shown = useCountUp(item.value, reduced);
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      style={{
        textAlign: 'left',
        fontFamily: 'inherit',
        background: colors.surface,
        border: '1px solid ' + colors.border,
        borderRadius: '0.9rem',
        padding: '0.75rem',
        cursor: 'pointer',
        minHeight: open ? '7.5rem' : '5.4rem',
      }}
    >
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: '0.35rem' }}>
        <span style={{ fontSize: '1.35rem', fontWeight: 700, color: colors.text }}>{shown}</span>
        {item.beats ? <span className="agent-stats-celebrate" style={{ color: ORANGE, fontSize: '0.85rem' }} aria-hidden="true">✦</span> : null}
      </span>
      <span style={{ display: 'block', marginTop: '0.15rem', fontSize: '0.78rem', fontWeight: 650, color: colors.text }}>{item.label}</span>
      {item.trend ? (
        <span style={{ display: 'block', marginTop: '0.15rem', fontSize: '0.68rem', color: item.beats ? (isDark ? '#fdba74' : ORANGE_INK) : colors.textSecondary }}>{item.trend}</span>
      ) : null}
      {open && item.breakdown && item.breakdown.kind === 'bars' ? <Bars rows={item.breakdown.rows} colors={colors} /> : null}
      {open && item.breakdown && item.breakdown.kind === 'spark' ? <Sparkline points={item.breakdown.points} /> : null}
      {open && item.breakdown && item.breakdown.kind === 'note' ? (
        <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.68rem', color: colors.textSecondary }}>{item.breakdown.text}</span>
      ) : null}
    </button>
  );
}

function TileGrid({ items, openId, onToggle, reduced, colors, isDark }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem' }}>
      {items.map((item) => (
        <StatTile
          key={item.id}
          item={item}
          open={openId === item.id}
          onToggle={() => onToggle(item.id)}
          reduced={reduced}
          colors={colors}
          isDark={isDark}
        />
      ))}
    </div>
  );
}

function Leaderboard({ rows, page, setPage, onOpen, colors, isDark }) {
  const panels = [
    { id: 'vendors', title: 'Top vendors', metric: 'contact', rows: rows.vendors || [] },
    { id: 'homeowners', title: 'Most active homeowners', metric: 'task', rows: rows.homeowners || [] },
  ];
  const panel = panels[page] || panels[0];
  return (
    <div>
      <div role="tablist" aria-label="Leaderboards" style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.45rem' }}>
        {panels.map((item, index) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={index === page}
            onClick={() => setPage(index)}
            style={{
              border: '1px solid ' + (index === page ? TEAL_BRIGHT : colors.border),
              background: index === page ? (isDark ? 'rgba(89, 196, 196, 0.16)' : 'rgba(89, 196, 196, 0.12)') : 'transparent',
              color: colors.text,
              borderRadius: '999px',
              padding: '0.28rem 0.6rem',
              fontSize: '0.72rem',
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {item.title}
          </button>
        ))}
      </div>
      <div
        onTouchStart={(event) => { event.currentTarget.dataset.x = String(event.changedTouches[0].clientX); }}
        onTouchEnd={(event) => {
          const start = Number(event.currentTarget.dataset.x || 0);
          const delta = event.changedTouches[0].clientX - start;
          if (delta < -30) setPage(1);
          if (delta > 30) setPage(0);
        }}
        style={{ background: colors.surface, border: '1px solid ' + colors.border, borderRadius: '0.9rem', padding: '0.35rem' }}
      >
        {panel.rows.length === 0 ? (
          <p style={{ margin: '0.45rem', color: colors.textSecondary, fontSize: '0.78rem' }}>Nothing in this range yet.</p>
        ) : panel.rows.map((row) => (
          <button
            key={row.name}
            type="button"
            onClick={() => onOpen && onOpen({ ...row, kind: panel.id === 'vendors' ? 'vendor' : 'client' })}
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              gap: '0.5rem',
              border: 'none',
              background: 'transparent',
              color: colors.text,
              fontFamily: 'inherit',
              padding: '0.55rem 0.45rem',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{ fontWeight: 650, fontSize: '0.86rem' }}>{row.name}</span>
            <span style={{ color: colors.textSecondary, fontSize: '0.75rem' }}>{countWord(row.count, panel.metric)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: '0.9rem' }}>
      <h3 style={{ margin: '0 0 0.45rem', fontSize: '0.78rem', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 700 }}>{title}</h3>
      {children}
    </section>
  );
}

function AgentHome({ activity, period, onPeriod, banner, onBilling, onAddCustomer, onAddVendor, onOpenRecord, initialOpen, requests, requestError, onAcceptRequest, onDeclineRequest }) {
  const { colors, isDark } = useTheme();
  const reduced = prefersReducedMotion();
  const [openId, setOpenId] = useState(initialOpen || '');
  const [page, setPage] = useState(0);
  const model = activity || { engagement: [], results: [], business: [], vendors: [], homeowners: [] };
  const pill = {
    border: 'none',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.82rem',
    lineHeight: 1,
    padding: '0.55rem 0.9rem',
    borderRadius: '999px',
    fontFamily: 'inherit',
    cursor: 'pointer',
  };

  return (
    <div data-agent-home="1" data-period={period}>
      <style>{`
        @keyframes agent-stats-pop {
          0% { transform: scale(0.4); opacity: 0; }
          70% { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .agent-stats-celebrate { display: inline-block; animation: agent-stats-pop 0.6s ease; }
        @media (prefers-reduced-motion: reduce) {
          .agent-stats-celebrate { animation: none; }
        }
      `}</style>
      {banner && banner.length ? (
        <div role="status" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.65rem',
          margin: '0 0 0.75rem',
          padding: '0.45rem 0.7rem',
          borderRadius: '0.75rem',
          background: isDark ? 'rgba(245, 158, 11, 0.16)' : '#fff7ed',
          border: '1px solid ' + ORANGE,
        }}
        >
          <span style={{ color: isDark ? '#fdba74' : ORANGE_INK, fontSize: '0.78rem', lineHeight: 1.35, fontWeight: 600 }}>{banner.join(' ')}</span>
          <button type="button" onClick={onBilling} style={{ flex: '0 0 auto', border: 'none', background: 'transparent', color: isDark ? TEAL_BRIGHT : TEAL, fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>Billing</button>
        </div>
      ) : null}
      <JoinRequests
        requests={requests}
        error={requestError}
        onAccept={onAcceptRequest}
        onDecline={onDeclineRequest}
        colors={colors}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem' }}>
        <button type="button" onClick={onAddCustomer} style={{ ...pill, background: TEAL_BRIGHT }}>Add customer</button>
        <button type="button" onClick={onAddVendor} style={{ ...pill, background: ORANGE }}>Add vendor</button>
      </div>
      <div role="tablist" aria-label="Statistic range" style={{ display: 'flex', gap: '0.35rem', marginTop: '0.75rem' }}>
        {PERIODS.map((item) => {
          const selected = item.id === period;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => { setOpenId(''); setPage(0); onPeriod(item.id); }}
              style={{
                border: '1px solid ' + (selected ? TEAL_BRIGHT : colors.border),
                background: selected ? TEAL_BRIGHT : 'transparent',
                color: selected ? '#083344' : colors.text,
                borderRadius: '999px',
                padding: '0.35rem 0.7rem',
                fontWeight: 700,
                fontSize: '0.75rem',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      <Section title="Engagement">
        <TileGrid items={model.engagement} openId={openId} onToggle={(id) => setOpenId(openId === id ? '' : id)} reduced={reduced} colors={colors} isDark={isDark} />
      </Section>
      <Section title="Results">
        <TileGrid items={model.results} openId={openId} onToggle={(id) => setOpenId(openId === id ? '' : id)} reduced={reduced} colors={colors} isDark={isDark} />
      </Section>
      <Section title="Leaderboards">
        <Leaderboard rows={{ vendors: model.vendors, homeowners: model.homeowners }} page={page} setPage={setPage} onOpen={onOpenRecord} colors={colors} isDark={isDark} />
      </Section>
      <Section title="Your business">
        <TileGrid items={model.business} openId={openId} onToggle={(id) => setOpenId(openId === id ? '' : id)} reduced={reduced} colors={colors} isDark={isDark} />
      </Section>
    </div>
  );
}

export default AgentHome;
